import { useState, createContext, useContext } from "@wordpress/element";
import { __ } from "@wordpress/i18n";
import { Button } from "@/components/button";
import { Modal } from "@/components/modal";
import { css } from "@/utils/css";

interface ModalContextType {
    confirm: (content: React.ReactElement | string) => Promise<boolean>;
    openModal: (content: React.ReactElement | string) => void;
    closeModal: () => void;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export function ModalProvider({ children }: { children: React.ReactNode }) {
    const [open, setOpen] = useState(false);
    const [content, setContent] = useState<React.ReactElement | string>("");

    const closeModal = () => {
        setOpen(false);
        setContent(<></>);
    };

    const openModal = (content: React.ReactElement | string) => {
        setOpen(true);
        setContent(content);
    };

    const confirm = async (content: React.ReactElement | string): Promise<boolean> => {
        const confirmed: boolean = await new Promise((resolve) => {
            openModal(
                <ConfirmModal onConfirm={() => resolve(true)} onCancel={() => resolve(false)}>
                    {content}
                </ConfirmModal>
            );
        });

        closeModal();

        return confirmed;
    };

    return (
        <ModalContext.Provider value={{ confirm, openModal, closeModal }}>
            {children}
            {open && <Modal handleClose={closeModal}>{content}</Modal>}
        </ModalContext.Provider>
    );
}

export function useModal() {
    const context = useContext(ModalContext);
    if (!context) {
        throw new Error("useModal must be used within a ModalProvider");
    }
    return context;
}

function ConfirmModal({ children, onConfirm, onCancel }: React.PropsWithChildren<{ onConfirm?: () => void; onCancel?: () => void }>) {
    return (
        <>
            <div className={css("modal-header")}>
                <h2 className={css("modal-title")}>{__("Are you sure?", "antimanual")}</h2>
            </div>
            <div className={css("modal-body")}>{children}</div>
            <div className={css("modal-footer pr-10")}>
                <div className={css("footer-actions")}>
                    <Button variant="primary" className={css("w-full")} onClick={onConfirm}>
                        {__("Confirm", "antimanual")}
                    </Button>
                    <Button variant="secondary" className={css("w-full")} onClick={onCancel}>
                        {__("Cancel", "antimanual")}
                    </Button>
                </div>
            </div>
        </>
    );
}
